import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Loader2, PhoneCall, MessageCircle, KeyRound, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/**
 * Emergency Call Modal — PIN-only entry. WhatsApp-styled phone mockup.
 * On valid PIN -> log user in and navigate to /emergency.
 */
export default function EmergencyCallModal({ open, onClose }) {
  const navigate = useNavigate();
  const { loginWithPin } = useAuth();
  const [pin, setPin] = useState("");
  const [connecting, setConnecting] = useState(false);

  if (!open) return null;

  const onChangePin = (val) => {
    // Only digits, max 8
    const clean = val.replace(/\D/g, "").slice(0, 8);
    setPin(clean);
  };

  const startCall = async (e) => {
    e?.preventDefault?.();
    if (!pin || pin.length < 4) {
      toast.error("Ingresa tu PIN (mínimo 4 dígitos)");
      return;
    }
    setConnecting(true);
    try {
      await loginWithPin(pin);
      onClose && onClose();
      navigate("/emergency");
    } catch (err) {
      const code = err.response?.status;
      let msg;
      if (code === 429) {
        msg = err.response?.data?.detail || "Demasiados intentos. Espera 15 minutos.";
      } else {
        msg = err.response?.data?.detail || "PIN no encontrado";
      }
      toast.error(msg, { duration: 5000 });
    } finally {
      setConnecting(false);
    }
  };

  const goBuy = () => {
    onClose && onClose();
    navigate("/precios");
  };

  return (
    <div
      data-testid="emergency-call-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-zinc-950/80 backdrop-blur-md animate-fade-in"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="emergency-call-close"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-zinc-900/10 hover:bg-zinc-900/20 flex items-center justify-center transition-colors"
        >
          <X className="w-5 h-5 text-zinc-900" />
        </button>

        {/* Phone mockup - WhatsApp green */}
        <div className="bg-gradient-to-b from-[#075E54] via-[#128C7E] to-[#25D366] px-8 pt-10 pb-8 flex flex-col items-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 right-10 w-32 h-32 bg-white rounded-full blur-2xl" />
            <div className="absolute bottom-10 left-10 w-40 h-40 bg-white rounded-full blur-3xl" />
          </div>

          <div className="relative w-48 h-80 bg-zinc-900 rounded-[2.5rem] border-[6px] border-zinc-800 shadow-2xl flex flex-col overflow-hidden">
            <div className="w-20 h-5 bg-zinc-900 rounded-b-2xl mx-auto" />
            <div className="flex-1 bg-gradient-to-b from-[#128C7E] to-[#075E54] flex flex-col items-center justify-between p-4 pt-6">
              <div className="text-center">
                <p className="text-[10px] uppercase tracking-widest text-white/70 font-bold">
                  WhatsApp
                </p>
                <p className="text-white text-sm font-black mt-2">miapoyo · IA</p>
                <p className="text-white/70 text-[11px] mt-1">Llamada entrante…</p>
              </div>
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-white/30 animate-ping" />
                <div className="absolute inset-0 rounded-full bg-white/20 scale-150 animate-ping" style={{ animationDelay: "0.3s" }} />
                <div className="relative w-20 h-20 rounded-full bg-white flex items-center justify-center shadow-xl">
                  <MessageCircle className="w-10 h-10 text-[#25D366]" fill="currentColor" />
                </div>
              </div>
              <div className="flex items-center justify-center gap-1 h-6 mb-1">
                {[8, 14, 20, 12, 18, 10, 22, 14, 16, 10, 18, 12].map((h, i) => (
                  <div
                    key={`emerg-modal-bar-${i}`}
                    className="w-0.5 bg-white/80 rounded-full wave-bar"
                    style={{ height: `${h}px`, animationDelay: `${i * 0.08}s` }}
                  />
                ))}
              </div>
              <div className="flex items-center justify-center gap-3 pb-1">
                <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center shadow-lg">
                  <PhoneCall className="w-5 h-5 text-white rotate-[135deg]" />
                </div>
                <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center shadow-lg animate-pulse">
                  <PhoneCall className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>
          </div>

          <p className="relative text-white text-center text-base font-bold mt-5 leading-snug px-4">
            Te conectamos con el asistente de voz en segundos
          </p>
        </div>

        {/* PIN-only form */}
        <form onSubmit={startCall} className="p-6 sm:p-7">
          <p
            data-testid="emergency-call-instruction"
            className="text-center text-zinc-900 font-bold text-base sm:text-lg mb-2 leading-snug"
          >
            Ingresa tu PIN para enlazar la llamada
          </p>
          <p className="text-center text-xs text-zinc-500 mb-5 leading-snug">
            Es el número que te entregamos cuando se acreditó tu pago.
          </p>

          <div className="mb-5">
            <div className="relative">
              <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
              <Input
                data-testid="emergency-call-pin"
                value={pin}
                onChange={(e) => onChangePin(e.target.value)}
                placeholder="------"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                disabled={connecting}
                maxLength={8}
                className="h-20 pl-14 pr-4 text-center text-4xl font-black tracking-[0.6em] rounded-2xl border-2 border-zinc-300 focus:border-[#25D366] font-mono"
              />
            </div>
          </div>

          <Button
            data-testid="emergency-call-submit"
            type="submit"
            disabled={connecting || pin.length < 4}
            className="w-full h-16 text-lg font-black uppercase rounded-xl bg-[#25D366] hover:bg-[#128C7E] disabled:opacity-50 text-white shadow-[0_10px_30px_rgba(37,211,102,0.4)] flex items-center justify-center gap-3 transition-colors"
          >
            {connecting ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin" />
                Conectando…
              </>
            ) : (
              <>
                <PhoneCall className="w-6 h-6" />
                Iniciar llamada con la IA
              </>
            )}
          </Button>

          <div className="mt-5 pt-5 border-t border-zinc-200 text-center">
            <p className="text-sm text-zinc-700 leading-snug mb-2">
              ¿Aún no tienes PIN?
            </p>
            <button
              type="button"
              onClick={goBuy}
              data-testid="emergency-call-buy-plan"
              className="inline-flex items-center gap-1.5 text-sm font-black text-[#25D366] hover:text-[#128C7E] underline"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Compra tu plan y recibe tu PIN
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
